import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, PlayCircle, ShieldCheck } from "lucide-react";
import { KineticLines } from "./RevealText";
import { heroMetricValues } from "../data/staticData";
import { useLanguage } from "../i18n/LanguageContext";

export function Hero({ onOpenDemo }) {
  const { t, language } = useLanguage();
  const heroContent = t.hero;
  // ru/es/de/pt have long words that need a smaller headline to avoid
  // overflowing into the hero photo; ru needs an extra 5% on top of that.
  const heroTitleSizeClass =
    language === "ru"
      ? "text-[1.71rem] sm:text-[2.28rem] lg:text-[3.496rem]"
      : ["es", "de", "pt"].includes(language)
      ? "text-[1.8rem] sm:text-[2.4rem] lg:text-[3.68rem]"
      : "text-4xl sm:text-5xl lg:text-[4.6rem]";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const visualScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const fade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  const [emotionIdx, setEmotionIdx] = useState(0);
  const [stressVal, setStressVal] = useState(24);

  useEffect(() => {
    const emoInterval = setInterval(
      () => setEmotionIdx((i) => (i + 1) % heroContent.emotionValues.length),
      2600
    );
    const stressOptions = [18, 24, 32, 41, 27];
    const stressInterval = setInterval(
      () => setStressVal((v) => stressOptions[(stressOptions.indexOf(v) + 1) % stressOptions.length] ?? stressOptions[0]),
      2200
    );
    return () => {
      clearInterval(emoInterval);
      clearInterval(stressInterval);
    };
  }, [heroContent]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden bg-[#050505] pt-28 pb-20"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 grid-line opacity-40" />
      <div className="absolute inset-0 scan-lines" />
      <div className="absolute -top-40 left-1/3 w-[600px] h-[600px] rounded-full bg-[#00F0FF]/10 blur-[120px]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/5 mb-8"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-pulse" />
            <span className="font-mono-data text-xs text-[#00F0FF] tracking-wide">{heroContent.badge}</span>
          </motion.div>

          <h1
            className={`font-display font-black tracking-tighter hyphens-auto break-words leading-[1.02] ${heroTitleSizeClass}`}
          >
            <KineticLines
              lines={[heroContent.title, heroContent.titleLine2, heroContent.titleHighlight]}
              className="text-white"
              highlightIndex={2}
            />
          </h1>
          <span className="block h-2" />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-8 text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed"
            data-testid="hero-description"
          >
            {heroContent.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onOpenDemo}
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-[#00F0FF] text-black font-semibold hover:brightness-110 hover:scale-[1.03] transition-all duration-200"
            >
              {t.site.ctaPrimary}
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="#live-demo"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md border border-white/15 text-white font-semibold hover:border-[#00F0FF]/50 hover:text-[#00F0FF] transition-colors duration-200"
            >
              <PlayCircle className="w-4 h-4" />
              {heroContent.ctaSecondary}
            </a>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#00F0FF]/20 bg-[#00F0FF]/5 font-mono-data text-xs text-zinc-400"
              data-testid="google-play-availability-badge"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-[#00F0FF]" strokeWidth={1.75} />
              {t.site.googlePlayBadge}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-8"
            data-testid="hero-metrics"
          >
            {heroContent.metricLabels.map((label, i) => (
              <div key={label}>
                <div className="font-mono-data text-2xl sm:text-3xl font-bold text-white">{heroMetricValues[i]}</div>
                <div className="text-xs text-zinc-500 mt-1">{label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: visualY, scale: visualScale }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-2xl overflow-hidden border border-white/10"
            data-testid="hero-visual"
          >
            <img
              src="https://customer-assets-cm19k8pv.emergentagent.net/job_web-complete-check/artifacts/6plwtmjy_5397944670786099703.jpg"
              alt="NeuroScan biometric face scan"
              className="w-full h-[520px] object-cover"
              style={{ filter: "saturate(0.45) contrast(1.08) brightness(0.92)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(circle at 50% 38%, transparent 32%, rgba(5,5,5,0.82) 88%)" }}
            />
            <div className="absolute inset-0 scan-lines" />

            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
              <motion.line
                y1="16" y2="72" stroke="#FF2D2D" strokeWidth="0.35"
                style={{ filter: "drop-shadow(0 0 3px rgba(255,45,45,0.8))" }}
                initial={{ x1: 38, x2: 38, opacity: 0.5 }}
                animate={{ x1: [38, 60, 38], x2: [38, 60, 38], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.line
                x1="22" x2="78" stroke="#FF2D2D" strokeWidth="0.35"
                style={{ filter: "drop-shadow(0 0 3px rgba(255,45,45,0.8))" }}
                initial={{ y1: 24, y2: 24, opacity: 0.5 }}
                animate={{ y1: [24, 62, 24], y2: [24, 62, 24], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 3.1, repeat: Infinity, ease: "easeInOut" }}
              />
            </svg>

            <div className="absolute top-5 left-5 font-mono-data text-[10px] text-[#00F0FF] px-2 py-1 rounded border border-[#00F0FF]/30 bg-black/60">
              REC ● LIVE
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.6 }}
              className="absolute bottom-5 left-5 right-5 rounded-lg border border-white/10 bg-black/60 backdrop-blur-xl px-4 py-3"
            >
              <div className="flex items-center justify-between font-mono-data text-xs">
                <span className="text-zinc-400">{heroContent.trustIndexLabel}</span>
                <span className="text-[#00FF66] font-bold">87%</span>
              </div>
              <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "87%" }}
                  transition={{ delay: 1.4, duration: 1 }}
                  className="h-full bg-[#00FF66]"
                />
              </div>

              <div
                className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between"
                data-testid="hero-voice-indicator"
              >
                <span className="font-mono-data text-[10px] text-zinc-400 uppercase tracking-wide">
                  {heroContent.voiceLabel}
                </span>
                <div className="flex items-end gap-[3px] h-4">
                  {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <motion.span
                      key={i}
                      className="w-[3px] rounded-full bg-[#00F0FF]"
                      style={{ filter: "drop-shadow(0 0 2px rgba(0,240,255,0.7))" }}
                      initial={{ height: 4 }}
                      animate={{ height: [4, 6 + (i % 4) * 3, 3, 14 - (i % 3) * 3, 5] }}
                      transition={{ duration: 1.1 + (i % 3) * 0.15, repeat: Infinity, ease: "easeInOut", delay: i * 0.08 }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="absolute -bottom-6 -left-6 flex gap-2 flex-wrap max-w-[80%]">
            {heroContent.trustBadges.map((b) => (
              <span
                key={b}
                className="font-mono-data text-[10px] px-3 py-1.5 rounded-full border border-white/15 bg-[#0F0F11] text-zinc-400"
              >
                {b}
              </span>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="hidden sm:block absolute -top-5 -right-5 lg:-right-9 rounded-lg border border-white/10 bg-black/70 backdrop-blur-xl px-4 py-2.5 z-20 shadow-lg shadow-black/40"
            data-testid="hero-emotion-card"
          >
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-pulse" />
              <span className="font-mono-data text-[10px] text-zinc-400 uppercase tracking-wide">
                {heroContent.emotionLabel}
              </span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={emotionIdx}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.3 }}
                className="font-display font-semibold text-sm text-white mt-0.5"
              >
                {heroContent.emotionValues[emotionIdx]}
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.6, duration: 0.6 }}
            className="hidden sm:block absolute top-[36%] -left-6 lg:-left-11 rounded-lg border border-white/10 bg-black/70 backdrop-blur-xl px-4 py-3 z-20 w-36 shadow-lg shadow-black/40"
            data-testid="hero-stress-card"
          >
            <div className="flex items-center justify-between font-mono-data text-[10px]">
              <span className="text-zinc-400 uppercase tracking-wide">{heroContent.stressLabel}</span>
              <span className="text-[#FF8A3D] font-bold">{stressVal}%</span>
            </div>
            <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
              <motion.div
                animate={{ width: `${stressVal}%` }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                className="h-full bg-[#FF8A3D]"
              />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
